import React, { Fragment } from 'react';
import { Button, IconButton, TextField, Typography, Box } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { MaterialGroup, GroupMaterials } from 'models/organization';

type MaterialGroupDialogProps = {
    open: boolean;
    onClose: () => void;
    onSave: (group: MaterialGroup) => void;
    onDelete?: (groupId: string) => void;
    materialGroup: MaterialGroup;
    setMaterialGroup: (group: MaterialGroup) => void;
    groupMaterials: { material: string }[];
    setGroupMaterials: (materials: GroupMaterials[]) => void;
};

const MaterialGroupDialog: React.FC<MaterialGroupDialogProps> = ({
    open,
    onClose,
    onSave,
    onDelete,
    materialGroup,
    setMaterialGroup,
    groupMaterials,
    setGroupMaterials,
}) => {
    const { t: translate } = useTranslation(['manage-actions', 'common', 'orders']);
    const handleAddMaterial = () => {
        setGroupMaterials([...groupMaterials, { material: '' }]);
    };

    const handleDeleteMaterial = (index: number) => {
        const updated = [...groupMaterials];
        updated.splice(index, 1);
        setGroupMaterials(updated);
    };

    const handleMaterialChange = (index: number, value: string) => {
        const updated = JSON.parse(JSON.stringify(groupMaterials));
        updated[index].material = value;
        setGroupMaterials(updated);
    };

    const isSaveDisabled = !materialGroup.name || groupMaterials?.some((m) => !m.material);

    const handleSave = () => {
        onSave({ ...materialGroup, materials: groupMaterials });
    };

    return (
        <DHDialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            title={
                materialGroup._id
                    ? translate('manage-actions:EDIT_MATERIAL_GROUP')
                    : translate('manage-actions:ADD_MATERIAL_GROUP')
            }
            actions={
                <Fragment>
                    {materialGroup._id && onDelete && (
                        <Box mr={1}>
                            <Button
                                onClick={() => onDelete(materialGroup._id)}
                                color="error"
                                startIcon={<DeleteIcon />}
                            >
                                {translate('common:DELETE')}
                            </Button>
                        </Box>
                    )}
                    <Button disabled={isSaveDisabled} onClick={handleSave} variant="contained">
                        {translate('common:SAVE')}
                    </Button>
                </Fragment>
            }
        >
            <Box display="flex" flexDirection="column" gap={1} mt={1}>
                <Box mb={2}>
                    <TextField
                        label={translate('common:NAME')}
                        value={materialGroup.name}
                        onChange={(e) => setMaterialGroup({ ...materialGroup, name: e.target.value })}
                        fullWidth
                    />
                </Box>

                <Typography variant="h4">{translate('orders:MATERIALS')}:</Typography>

                {!groupMaterials.length && !materialGroup._id ? (
                    <TextField
                        placeholder={translate('manage-actions:ENTER_MATERIAL_NAME')}
                        value={groupMaterials[0]?.material || ''}
                        onChange={(e) => setGroupMaterials([{ material: e.target.value }])}
                        fullWidth
                    />
                ) : (
                    groupMaterials.map((mat, index) => (
                        <Box key={index} display="flex" gap={1} alignItems="center">
                            <TextField
                                value={mat.material}
                                placeholder={translate('manage-actions:ENTER_MATERIAL_NAME')}
                                onChange={(e) => handleMaterialChange(index, e.target.value)}
                                fullWidth
                            />
                            <IconButton onClick={() => handleDeleteMaterial(index)} color="error">
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    ))
                )}

                <Box>
                    <Button onClick={handleAddMaterial}>+ {translate('common:ADD')}</Button>
                </Box>
            </Box>
        </DHDialog>
    );
};

export default MaterialGroupDialog;
